import { computed, ref } from "vue";
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";

import { useApplicationStore } from "@/stores/Application";
import { usePermissionsStore } from "@/stores/Permissions";
import { normalizeTradeAlert } from "@/services/data/sync/trade-alerts-sync-coordinator";
import { hydrateContent } from "@/services/data/sync/content-hydration";
import { isFreelyAvailableContent } from "@/utils/access-utils";
import { formatDateAsISO8601String, normalizeDateTime, utcNow } from "@tmi/utils/date-time";

import { goFetch } from "@/utils/network-utils";
import { clientStateManager } from "@/services/error-services";
import type { PersistedQueryPayload, QueryFilter } from "@tmi/types/api/fetch";

import LocalForagePlugin, { tradeAlerts } from "@/plugins/LocalForagePlugin";
import type { TradeAlerts } from "@tmi/types/api/content-models";
import {
    Category,
    ContentfulItem,
    ContentfulTradeAlert,
    Countable,
    NormalizedContent,
    Product,
    TradeAlert
} from "@tmi/types/entities";

// Non-reactive variables
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
import { useAuthStore } from "@/stores/Auth";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

 // State values
const isLoading = computed< boolean >( () => loading.value || fetching.value );

const initialized = computed< boolean >( () => allTradeAlerts.value.length > 0 );
const fetching = ref< boolean >( false );
const loading = ref< boolean >( false );

const allTradeAlertIds = new Map< string, boolean >();
const allTradeAlertsBySlugMap = new Map< string, TradeAlert >();
const allTradeAlerts = ref< TradeAlert[] >( [] as unknown as TradeAlert[] );

export const useTradeAlertsStore = defineStore( 'tradeAlerts', () => {
    const appStore = useApplicationStore();
    // const { apiStatus } = storeToRefs( appStore );

    const { authToken, isAuthenticated } = storeToRefs( useAuthStore() );
    const permissionsStore = usePermissionsStore();

    /**
     * Clear all state values for this store
     */
    function reset(): void {
        allTradeAlerts.value.splice( 0 );
        allTradeAlertsBySlugMap.clear();
        allTradeAlertIds.clear();
        loading.value = false;
        fetching.value = false;
    }

    function initialize(): void {
        if ( !isAuthenticated.value || loading.value ) {
            logger.debug( `[ TradeAlerts ].initialize() exiting - [ authenticated: ${ isAuthenticated.value } ] or [ already processing: ${ loading.value } ]...` );
            return;
        }

        loading.value = true;
        logger.debug( `[ TradeAlerts ] attempting to load tradeAlerts...` );

        const t0: number = performance.now();

        if ( typeof tradeAlerts === 'undefined' ) {
            // Wait for the driver to settle
            LocalForagePlugin.initialize();
        }

        tradeAlerts.ready()
            .then( () => {
                tradeAlerts.length()
                    .then( ( val: number ) => {
                        if ( val > 0 ) {
                            const loadedTradeAlerts: TradeAlert[] = [];
                            // Iterate over all elements within IDB
                            tradeAlerts.iterate( ( item: NormalizedContent ) => {
                                loadedTradeAlerts.push( hydrateContent( item, appStore.compositionCtx ) );
                                return;
                            } ).then( () => {
                                mergeFilterAndSortTradeAlertCatalog( loadedTradeAlerts );

                                const t1: number = performance.now();
                                logger.info( `(local) Loaded ${ loadedTradeAlerts.length } trade alerts in ${ t1 - t0 } ms` );
                            } )
                                .finally( () => loading.value = false );
                        } else {
                            refreshFromServer( formatDateAsISO8601String( normalizeDateTime( '2023-01-01' ) ?? utcNow() ) )
                                .then( ( tradeAlerts: TradeAlert[] ) => {
                                    mergeFilterAndSortTradeAlertCatalog( tradeAlerts );

                                    const t1: number = performance.now();
                                    logger.log( `Fetching & mapping ${ tradeAlerts.length } articles took ${ t1 - t0 } ms` );
                                } )
                                .finally( () => loading.value = false );
                        }
                    } )
                    .catch( async ( err: any ) => {
                        // If anything went wrong while attempting to initialize the store, reset it and then handle the specific error
                        reset();

                        clientStateManager.queueClientError( err, {} );
                    } );
            } );
    }

    async function refreshFromServer( latestUtcDate: string ): Promise< TradeAlert[] > {
        // Timing - testing to see how expensive each method is
        const t0: number = performance.now();

        const queryVariables: QueryFilter = {
            skip: 0,
            limit: 50,
            contentFilter: {
                fromPublishedDateTimeUTC: latestUtcDate
            }
        };

        const response =
            await goFetch< TradeAlerts, PersistedQueryPayload >( {
                endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                payload: {
                    id: '210dba7500d8d488244bae6c7abe7efc',
                    variables: queryVariables
                },
                authToken: authToken.value
            } );

        const receivedTradeAlerts: TradeAlert[] = [];
        if ( null != response?.data?.tradeAlertCatalog?.trade_alerts ) {
            response.data.tradeAlertCatalog.trade_alerts
                .map( ( alert: ContentfulTradeAlert ) => {
                    const normalized = normalizeTradeAlert( alert );
                    if ( normalized ) {
                        receivedTradeAlerts.push( hydrateContent( normalized, appStore.compositionCtx ) );
                    }
                } );

            mergeFilterAndSortTradeAlertCatalog( receivedTradeAlerts );

            const t1: number = performance.now();
            logger.debug( `Fetching & mapping ${ receivedTradeAlerts.length } trade alert items from server in ${ t1 - t0 } ms` );
        }

        return receivedTradeAlerts;
    }

    async function fetchTradeAlertBySlugIfMissing( slug: string ): Promise< TradeAlert | undefined > {
        // Best case scenario is that the article has already been loaded and is immediately available
        let requestedTradeAlert: TradeAlert | undefined = allTradeAlertsBySlugMap.get( slug );
        if ( requestedTradeAlert ) {
            return requestedTradeAlert;
        }

        try {
            fetching.value = true;

            const response =
                await goFetch< TradeAlerts, PersistedQueryPayload >( {
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: '210dba7500d8d488244bae6c7abe7efc',
                        variables: {
                            skip: 0,
                            limit: 1,
                            contentFilter: {
                                slugs: [ slug ]
                            }
                        }
                    },
                    authToken: authToken.value
                } );

            const receivedTradeAlerts: ContentfulTradeAlert[] | undefined = response?.data?.tradeAlertCatalog?.trade_alerts;
            if ( receivedTradeAlerts && receivedTradeAlerts.length > 0 ) {
                const normalized = normalizeTradeAlert( receivedTradeAlerts[ 0 ] );
                requestedTradeAlert = normalized ? hydrateContent( normalized, appStore.compositionCtx ) : undefined;

                if ( typeof requestedTradeAlert !== 'undefined' ) {
                    mergeFilterAndSortTradeAlertCatalog( [ requestedTradeAlert ] );
                }
            }
        } catch ( e ) {
            logger.error( JSON.stringify( e ) );
            return undefined;
        }
        finally {
            fetching.value = false;
        }

        return requestedTradeAlert;
    }

    /**
     * Retrieves an ITradeAlert object by its slug value or returns undefined if the given
     * slug value is not found within the in-memory collection.
     *
     * @param {string} id - the unique sys.id value for the trade alert content item
     * @returns {TradeAlert | undefined} - the ITradeAlert object or undefined if the slug is not
     * found within the local collection of ITradeAlert objects
     */
    function tradeAlertById( id: string ): TradeAlert | undefined {
        return allTradeAlerts.value.find( ( tradeAlert: TradeAlert ): boolean => tradeAlert.contentfulId === id );
    }

    /**
     * Retrieves an ITradeAlert object by its slug value or returns undefined if the given
     * slug value is not found within the in-memory collection.
     *
     * @param {string} slug - the unique url-friendly string that identifies the ITradeAlert
     * @returns {TradeAlert | undefined} - the ITradeAlert object or undefined if the slug is not
     * found within the local collection of ITradeAlert objects
     */
    function tradeAlertBySlug( slug: string ): TradeAlert | undefined {
        return allTradeAlerts.value.find( ( tradeAlert: TradeAlert ): boolean => tradeAlert.slug === slug );
    }

    function nMostRecentlyPublishedTradeAlerts( tradeAlertCount: number ): TradeAlert[] {
        return allTradeAlerts.value.slice( 0, tradeAlertCount );
    }

    /**
     * Performs a merge and sort (based on publication date and time of the ITradeAlert) with the current collection of trade
     * alerts and any new trade alerts that are loaded.
     *
     * @param {TradeAlert[]} newAlerts - the newly loaded collection of ITradeAlert objects that need to be added
     * @returns {TradeAlert[]} - the aggregated collection of ITradeAlert objects sorted by their publication date & time
     */
    function mergeFilterAndSortTradeAlertCatalog( newAlerts: TradeAlert[] ): void {
        logger.debug( '[ TradeAlerts ] mergeAndSortTradeAlertCatalog - existing: ', allTradeAlerts.value.length, ', new: ', newAlerts.length );

        const uniqueTradeAlerts = new Map( allTradeAlerts.value.map( ( tradeAlert: TradeAlert ) => [ tradeAlert.contentfulId, tradeAlert ] ) );
        newAlerts.forEach( ( newAlert: TradeAlert ): void => {
            // Always overwrite whatever was previously in the collection (fucking crazy editing process, why fix it
            // when you can just make everything else more fucking complicated)
            uniqueTradeAlerts.set( newAlert.contentfulId, newAlert );
        } );

        allTradeAlerts.value = [ ...( Array.from( uniqueTradeAlerts.values() ) )
            // Filter out any content to which the current user has not been granted access
            .filter( ( alert: TradeAlert ) => {
                if ( !isFreelyAvailableContent( alert.products.map( p => p.slug ) ) ) {
                    return isAuthenticated.value && alert.products.some( ( p: Product ) =>
                        !!( p.codes ) && permissionsStore.hasAccessBasedOnShortCode( p.codes! )
                    );
                }

                return true;
            } )
            .sort( ( ta1: TradeAlert, ta2: TradeAlert ) =>
                ta2.publishedDate?.localeCompare( ta1.publishedDate ) )
        ];

        logger.debug( '[ TradeAlerts ] mergeAndSortTradeAlertCatalog - combined: ', allTradeAlerts.value.length );
    }

    // This is implemented as a computed property to avoid unnecessarily calculating it multiple times
    const tradeAlertCategories = computed< Category[] >( () => {
        const categoryCountMap: Map< string, Countable< Category > > = new Map< string, Countable< Category > >();
        allTradeAlerts.value.forEach( ( item: ContentfulItem ): void => {
            item.categories.forEach( ( category: Category ): void => {
                if ( !categoryCountMap.has( category.slug ) ) {
                    categoryCountMap.set( category.slug, {
                        item: category,
                        count: 0
                    } );
                }

                categoryCountMap.get( category.slug )!.count++;
            } );
        } );

        const categoriesByObservationCount: Countable< Category >[] = [ ...categoryCountMap.values() ];
        const categoriesByAscendingObservationCount: Countable< Category >[] =
            categoriesByObservationCount.sort( ( container1: Countable< Category >, container2: Countable< Category > ) => container1.count - container2.count );

        return categoriesByAscendingObservationCount.map( ( container: Countable< Category > ) => container.item );
    } );

    return {
        // ACTIONS
        fetchTradeAlertBySlugIfMissing,
        initialize,
        nMostRecentlyPublishedTradeAlerts,
        reset,
        tradeAlertById,
        tradeAlertBySlug,

        // GETTERS
        allTradeAlerts,
        initialized,
        isLoading,
        tradeAlertCategories
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useTradeAlertsStore, import.meta.hot ) );
}
